/**
 * The Design pillar's git-sourced half
 * (specs/archive/design-pillar-reads-the-decisions).
 *
 * A workspace's decisions are ordinary files in its design repository: ADRs
 * in the repo's single ADR directory (`docs/design/adr/` canonical,
 * `docs/adr/` grandfathered — Documentation Standard §3.2) and design
 * documents under `docs/design/`. The sync worker indexes them like any other
 * document and stamps an ADR with its number and the state its header
 * declares; this module turns that index into the rows the Design list shows
 * beside the documents authored in the app. Nothing here reads GitHub: the
 * index is the listing, the reader opens the file.
 */
import { docsApi, type DocIndex } from '@/services/docsApi';

/** The folders of the design repository the pillar reads. */
export const DESIGN_FOLDERS = ['docs/design/', 'docs/adr/'] as const;

/** The states an ADR header may declare (Documentation Standard §3.2), plus
 *  `draft`, which the in-product ADR template writes before a decision. */
export const DECISION_STATUSES = ['proposed', 'accepted', 'rejected', 'superseded', 'deprecated', 'draft'] as const;
export type DecisionStatus = (typeof DECISION_STATUSES)[number];

/** One row of the listing — the index row is enough; the file is read when opened. */
export interface DesignDecision {
  id: string;
  kind: 'adr' | 'design-doc';
  title: string;
  repoFullName: string;
  path: string;
  /** The ADR's number from its file name; null for a design document or a number-less ADR. */
  number: number | null;
  /** The state the header declares; '' when it declares none the vocabulary knows. */
  status: string;
  syncedAt: string;
}

/** The largest listing one request returns — the API's own default is 50. */
const PAGE = 1000;

export function toDecision(doc: DocIndex): DesignDecision {
  const adr = doc.doc_type === 'adr';
  return {
    id: doc.id,
    kind: adr ? 'adr' : 'design-doc',
    title: doc.title || doc.path.split('/').pop() || doc.path,
    repoFullName: doc.repo_full_name,
    path: doc.path,
    number: adr && typeof doc.decision_number === 'number' ? doc.decision_number : null,
    status: adr ? (doc.decision_status ?? '') : '',
    syncedAt: doc.synced_at,
  };
}

/**
 * Newest decision first: ADRs by number descending, a number-less ADR after
 * the numbered ones, then design documents by sync time descending.
 */
export function sortDecisions(items: DesignDecision[]): DesignDecision[] {
  return [...items].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'adr' ? -1 : 1;
    if (a.kind === 'adr') {
      if (a.number === null && b.number === null) return 0;
      if (a.number === null) return 1;
      if (b.number === null) return -1;
      return b.number - a.number;
    }
    return b.syncedAt.localeCompare(a.syncedAt);
  });
}

/**
 * The design repository's decisions: its ADRs (the type index, wherever the
 * repo keeps its ADR directory) and everything under docs/design/ (a path
 * prefix), one row per file — an ADR under docs/design/adr/ answers both
 * queries and is kept once. Empty without a design repository.
 */
export async function loadDesignDecisions(designRepo: string): Promise<DesignDecision[]> {
  if (!designRepo) return [];
  const [adrs, designDocs] = await Promise.all([
    docsApi.listDocs({ repo: designRepo, type: 'adr', limit: PAGE, view: 'summary' }),
    docsApi.listDocs({ repo: designRepo, pathPrefix: 'docs/design/', limit: PAGE, view: 'summary' }),
  ]);
  const byPath = new Map<string, DesignDecision>();
  for (const doc of [...adrs, ...designDocs]) {
    if (!byPath.has(doc.path)) byPath.set(doc.path, toDecision(doc));
  }
  return sortDecisions([...byPath.values()]);
}

/** The book reader's path for a decision — where a file is read, edited and listened to. */
export function decisionReaderPath(d: Pick<DesignDecision, 'repoFullName' | 'path'>): string {
  const encodedPath = d.path.split('/').map(encodeURIComponent).join('/');
  return `/book/${d.repoFullName}/${encodedPath}`;
}

/**
 * A draft committed into the design repo (gitRepo/gitPath, ADR-033) is the
 * same file the index lists. The authored row wins — it opens the editor,
 * carries the lifecycle and publishes — so its indexed twin is left out.
 */
export function withoutAuthoredDuplicates(
  decisions: DesignDecision[],
  authored: ReadonlyArray<{ gitRepo?: string; gitPath?: string }>,
): DesignDecision[] {
  const committed = new Set(
    authored.filter((s) => s.gitRepo && s.gitPath).map((s) => `${s.gitRepo}#${s.gitPath}`),
  );
  if (committed.size === 0) return decisions;
  return decisions.filter((d) => !committed.has(`${d.repoFullName}#${d.path}`));
}
